import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { 
  Save, Loader2, Building2, Mail, Send, CheckCircle, XCircle, Plus, X, AlertTriangle, Bell, Eye, Clock, History, Trash2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

export default function EngagementsParametres() {
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingLaposte, setTestingLaposte] = useState(false);
  const [laposteStatus, setLaposteStatus] = useState(null);
  const [alertsPreview, setAlertsPreview] = useState(null);
  const [alertsHistory, setAlertsHistory] = useState(null);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [sendingAlerts, setSendingAlerts] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  
  const [settings, setSettings] = useState({
    entreprise_nom: "",
    entreprise_forme_juridique: "",
    entreprise_adresse: "",
    entreprise_siret: "",
    representant_legal: "",
    email_expediteur: "",
    emails_destinataires: [],
    alertes_actives: true,
    laposte_api_key: "",
    laposte_env: "sandbox"
  });
  
  const [newEmail, setNewEmail] = useState("");

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/api/engagements/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({
        entreprise_nom: res.data.entreprise_nom || "",
        entreprise_forme_juridique: res.data.entreprise_forme_juridique || "",
        entreprise_adresse: res.data.entreprise_adresse || "",
        entreprise_siret: res.data.entreprise_siret || "",
        representant_legal: res.data.representant_legal || "",
        email_expediteur: res.data.email_expediteur || "",
        emails_destinataires: res.data.emails_destinataires || [],
        alertes_actives: res.data.alertes_actives ?? true,
        laposte_api_key: "",  // Don't show existing key
        laposte_env: res.data.laposte_env || "sandbox"
      });
      setLaposteStatus(res.data.laposte_configured ? { configured: true } : null);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...settings };
      // Only send laposte_api_key if it was changed
      if (!payload.laposte_api_key) {
        delete payload.laposte_api_key;
      }
      
      await axios.put(`${API}/api/engagements/settings`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Paramètres enregistrés");
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleAddEmail = () => {
    if (!newEmail || !newEmail.includes("@")) {
      toast.error("Email invalide");
      return;
    }
    if (settings.emails_destinataires.includes(newEmail)) {
      toast.error("Email déjà ajouté");
      return;
    }
    setSettings(prev => ({
      ...prev,
      emails_destinataires: [...prev.emails_destinataires, newEmail]
    }));
    setNewEmail("");
  };

  const handleRemoveEmail = (email) => {
    setSettings(prev => ({
      ...prev,
      emails_destinataires: prev.emails_destinataires.filter(e => e !== email)
    }));
  };

  const handleTestLaposte = async () => {
    setTestingLaposte(true);
    try {
      const res = await axios.post(`${API}/api/engagements/laposte/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLaposteStatus(res.data);
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      setLaposteStatus({ success: false, message: "Erreur de connexion" });
      toast.error("Erreur lors du test");
    } finally {
      setTestingLaposte(false);
    }
  };

  const fetchAlertsPreview = async () => {
    setLoadingAlerts(true);
    try {
      const res = await axios.get(`${API}/api/engagements/alertes/preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlertsPreview(res.data);
    } catch (error) {
      console.error("Error fetching alerts:", error);
      toast.error("Erreur lors du chargement des alertes");
    } finally {
      setLoadingAlerts(false);
    }
  };

  const handleSendAlerts = async () => {
    if (!alertsPreview?.contracts_to_alert?.length) {
      toast.info("Aucune alerte à envoyer");
      return;
    }
    
    if (!window.confirm(`Envoyer les alertes pour ${alertsPreview.contracts_to_alert.length} contrat(s) à ${settings.emails_destinataires.join(", ")} ?`)) {
      return;
    }
    
    setSendingAlerts(true);
    try {
      const res = await axios.post(`${API}/api/engagements/alertes/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      // Refresh preview and history after sending
      fetchAlertsPreview();
      fetchAlertsHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSendingAlerts(false);
    }
  };

  const fetchAlertsHistory = async () => {
    try {
      const res = await axios.get(`${API}/api/engagements/alertes/history?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlertsHistory(res.data);
    } catch (error) {
      console.error("Error fetching alerts history:", error);
    }
  };

  const handleResetAlertsHistory = async () => {
    if (!window.confirm("Réinitialiser l'historique des alertes ? Cela permettra de renvoyer toutes les alertes.")) {
      return;
    }
    
    try {
      const res = await axios.delete(`${API}/api/engagements/alertes/reset`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchAlertsHistory();
      fetchAlertsPreview();
    } catch (error) {
      toast.error("Erreur lors de la réinitialisation");
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="engagements-parametres">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Paramètres</h1>
          <p className="text-slate-500">Configuration du module Dépenses avec Engagements</p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
      </div>

      {/* Entreprise */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Informations de l'entreprise expéditrice
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-500">
            Ces informations sont utilisées dans les lettres de résiliation générées.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nom de l'entreprise *</Label>
              <Input
                value={settings.entreprise_nom}
                onChange={e => setSettings({...settings, entreprise_nom: e.target.value})}
                placeholder="BE-TAYE"
              />
            </div>
            <div className="space-y-2">
              <Label>Forme juridique</Label>
              <Input
                value={settings.entreprise_forme_juridique}
                onChange={e => setSettings({...settings, entreprise_forme_juridique: e.target.value})}
                placeholder="SASU, SARL, SAS..."
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>SIRET</Label>
              <Input
                value={settings.entreprise_siret}
                onChange={e => setSettings({...settings, entreprise_siret: e.target.value})}
                placeholder="123 456 789 00012"
              />
            </div>
            <div className="space-y-2">
              <Label>Représentant légal *</Label>
              <Input
                value={settings.representant_legal}
                onChange={e => setSettings({...settings, representant_legal: e.target.value})}
                placeholder="Prénom NOM"
              />
              <p className="text-xs text-slate-500">Nom qui apparaîtra en signature des lettres</p>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Adresse complète</Label>
            <Input
              value={settings.entreprise_adresse}
              onChange={e => setSettings({...settings, entreprise_adresse: e.target.value})}
              placeholder="31 Boulevard Henri Sellier, 92150 Suresnes"
            />
          </div>
          <div className="space-y-2">
            <Label>Email expéditeur pour les résiliations *</Label>
            <Input
              type="email"
              value={settings.email_expediteur}
              onChange={e => setSettings({...settings, email_expediteur: e.target.value})}
              placeholder="resiliations@betaye.fr"
            />
            <p className="text-xs text-slate-500">
              Cet email sera utilisé comme expéditeur pour l'envoi des lettres de résiliation. 
              Il doit être un email vérifié sur votre domaine Resend.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Alertes Email */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Alertes email automatiques
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Activer les alertes</p>
              <p className="text-sm text-slate-500">Recevoir des rappels avant les dates limites de préavis</p>
            </div>
            <Switch
              checked={settings.alertes_actives}
              onCheckedChange={v => setSettings({...settings, alertes_actives: v})}
            />
          </div>
          
          <Separator />
          
          <div className="space-y-2">
            <Label>Emails destinataires</Label>
            <div className="flex gap-2">
              <Input
                type="email"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                placeholder="email@example.com"
                onKeyPress={e => e.key === "Enter" && handleAddEmail()}
              />
              <Button variant="outline" onClick={handleAddEmail}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            
            {settings.emails_destinataires.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {settings.emails_destinataires.map(email => (
                  <Badge key={email} variant="secondary" className="flex items-center gap-1">
                    {email}
                    <button onClick={() => handleRemoveEmail(email)} className="ml-1 hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
          
          <Alert className="bg-blue-50 border-blue-200">
            <AlertTriangle className="w-4 h-4 text-blue-600" />
            <AlertDescription className="text-blue-700">
              <strong>Seuils d'alerte :</strong> J-90, J-60, J-30, J-7 avant la date limite de préavis
            </AlertDescription>
          </Alert>

          <Separator />

          {/* Alerts Preview & Send */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium flex items-center gap-2">
                  <Bell className="w-4 h-4" />
                  Alertes en attente
                </p>
                <p className="text-sm text-slate-500">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Envoi automatique toutes les 6 heures
                  </span>
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setShowHistory(!showHistory); if (!showHistory && !alertsHistory) fetchAlertsHistory(); }}>
                  <History className="w-4 h-4" />
                  <span className="ml-1">Historique</span>
                </Button>
                <Button variant="outline" size="sm" onClick={fetchAlertsPreview} disabled={loadingAlerts}>
                  {loadingAlerts ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                  <span className="ml-1">Aperçu</span>
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleSendAlerts} 
                  disabled={sendingAlerts || !alertsPreview?.contracts_to_alert?.length || settings.emails_destinataires.length === 0}
                >
                  {sendingAlerts ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  <span className="ml-1">Envoyer</span>
                </Button>
              </div>
            </div>

            {/* Scheduler Status */}
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <AlertDescription className="text-green-700">
                <strong>Scheduler actif</strong> - Les alertes sont vérifiées automatiquement toutes les 6 heures et envoyées aux destinataires configurés.
              </AlertDescription>
            </Alert>

            {/* History Section */}
            {showHistory && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-50 px-4 py-2 border-b flex items-center justify-between">
                  <span className="text-slate-700 font-medium flex items-center gap-2">
                    <History className="w-4 h-4" />
                    Historique des envois
                  </span>
                  <Button variant="ghost" size="sm" onClick={handleResetAlertsHistory} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                    <Trash2 className="w-4 h-4 mr-1" />
                    Réinitialiser
                  </Button>
                </div>
                {alertsHistory?.alerts?.length > 0 ? (
                  <div className="divide-y max-h-48 overflow-y-auto">
                    {alertsHistory.alerts.map((a, idx) => (
                      <div key={idx} className="px-4 py-2 text-sm flex items-center justify-between hover:bg-slate-50">
                        <div>
                          <span className="font-medium">J-{a.alert_level}</span>
                          <span className="text-slate-500 ml-2">→ {a.sent_to?.join(", ")}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={a.automatic ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"}>
                            {a.automatic ? "Auto" : "Manuel"}
                          </Badge>
                          <span className="text-slate-400 text-xs">{formatDate(a.sent_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-6 text-center text-slate-500">
                    Aucun envoi enregistré
                  </div>
                )}
              </div>
            )}

            {alertsPreview && (
              <div className="border rounded-lg overflow-hidden">
                {alertsPreview.contracts_to_alert?.length > 0 ? (
                  <>
                    <div className="bg-amber-50 px-4 py-2 border-b flex items-center justify-between">
                      <span className="text-amber-800 font-medium">
                        {alertsPreview.contracts_to_alert.length} contrat(s) à alerter
                      </span>
                    </div>
                    <div className="divide-y max-h-64 overflow-y-auto">
                      {alertsPreview.contracts_to_alert.map(c => (
                        <div key={c.contrat_id} className="px-4 py-3 flex items-center justify-between hover:bg-slate-50">
                          <div>
                            <p className="font-medium text-slate-900">{c.nom_contrat}</p>
                            <p className="text-sm text-slate-500">{c.prestataire}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={
                              c.urgency === "critique" ? "bg-red-100 text-red-700" :
                              c.urgency === "urgent" ? "bg-amber-100 text-amber-700" :
                              "bg-blue-100 text-blue-700"
                            }>
                              J-{c.jours_restants}
                            </Badge>
                            <span className="text-sm text-slate-500">{c.date_limite_preavis}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="px-4 py-8 text-center text-slate-500">
                    <CheckCircle className="w-8 h-8 mx-auto text-green-500 mb-2" />
                    <p>Aucune alerte à envoyer</p>
                    <p className="text-sm">Tous les contrats sont à jour ou les alertes ont déjà été envoyées.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* La Poste */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="w-5 h-5" />
            Configuration La Poste (LRAR)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-500">
            Configurez l'API La Poste Okapi pour envoyer des lettres recommandées avec accusé de réception directement depuis l'application.
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Clé API Okapi</Label>
              <Input
                type="password"
                value={settings.laposte_api_key}
                onChange={e => setSettings({...settings, laposte_api_key: e.target.value})}
                placeholder={laposteStatus?.configured ? "••••••••" : "Entrez votre clé API"}
              />
            </div>
            <div className="space-y-2">
              <Label>Environnement</Label>
              <select
                className="w-full h-10 px-3 border rounded-md"
                value={settings.laposte_env}
                onChange={e => setSettings({...settings, laposte_env: e.target.value})}
              >
                <option value="sandbox">Sandbox (test)</option>
                <option value="production">Production</option>
              </select>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={handleTestLaposte} disabled={testingLaposte}>
              {testingLaposte ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Tester la connexion
            </Button>
            
            {laposteStatus && (
              <div className={`flex items-center gap-2 ${laposteStatus.success || laposteStatus.configured ? "text-green-600" : "text-red-600"}`}>
                {laposteStatus.success || laposteStatus.configured ? (
                  <><CheckCircle className="w-4 h-4" /> Connexion réussie</>
                ) : (
                  <><XCircle className="w-4 h-4" /> {laposteStatus.message}</>
                )}
              </div>
            )}
          </div>
          
          {!laposteStatus?.configured && !settings.laposte_api_key && (
            <Alert>
              <AlertTriangle className="w-4 h-4" />
              <AlertDescription>
                Sans clé API, l'envoi de LRAR ne sera pas disponible. Vous pourrez toujours générer les lettres et les télécharger en PDF.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
